import { Modal, CircularProgress } from "@mui/material";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./AddonModal.css";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { getGlobalProductsList } from "../../../../../../config/Services";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import { UserContext } from "../../../../../../App";

const AddonModal = ({
  addOnModalOpen,
  setAddOnModalOpen,
  productDetails,
  fetchVariantsFromAPI = false,
  selectedId,
  onAddItem,
  itemName,
  selectedAddOns,
}) => {
  const { saleMode } = useContext(UserContext);
  const [selectedVariant, setSelectedVariant] = useState("");
  const [selectedAddons, setSelectedAddons] = useState({});
  const [particularAddonsList, setParticularAddonsList] = useState([]);
  const [variantsFromApi, setVariantsFromApi] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [kitchenNotes, setKitchenNotes] = useState("");
  const [itemQuantity, setItemQuantity] = useState(1);

  // Pricing mode — same pattern as ComboModal
  const [pricingMode, setPricingMode] = useState("regular"); // regular | discount | complimentary
  const [discountType, setDiscountType] = useState("percent"); // percent | rupee
  const [discountValue, setDiscountValue] = useState("");

  // -1 = no keyboard focus; 0..variants-1 = variant focused; variants.. = addon focused
  const [focusedNavIndex, setFocusedNavIndex] = useState(-1);

  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const showSnackbar = useSnackbar();

  // ── Refs for stale-closure-safe keyboard handler ──────────────────────────
  const focusedNavIndexRef = useRef(-1);
  const selectedVariantRef = useRef("");
  const selectedAddonsRef = useRef({});
  const navigableVariantsRef = useRef([]);
  const filteredAddonsRef = useRef([]);
  const groupQuantitiesRef = useRef({});
  const handleAddItemRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!addOnModalOpen || !fetchVariantsFromAPI) return;
    setSelectedVariant(selectedId || "");
    setIsLoading(true);
    const payload = {
      page: 1,
      pageSize: 10,
      variantIds: selectedId ? [selectedId] : [],
      storeLocations: [locId],
      storeIds: [orgId]
    };
    getGlobalProductsList(payload)
      .then((res) => {
        if (res?.data?.status === "ERROR" || res?.data?.data?.es > 0) {
          showSnackbar(
            res?.data?.message ||
              res?.data?.data?.message ||
              "There was an error fetching the data",
            "error"
          );
          return;
        }
        const variants = res?.data?.data?.data?.data[0]?.variants || [];
        setVariantsFromApi(variants);
        const selectedVariantData = variants.find((v) => v?.variantId === selectedId);
        if (selectedAddOns?.length > 0 && variants.length > 0 && selectedVariantData?.addOn) {
          const preSelectedAddons = {};
          selectedAddOns.forEach((addOn) => {
            const matchingAddOn = selectedVariantData.addOn.find(
              (item) => item?.title === addOn?.itemName
            );
            if (matchingAddOn?.addOnId) {
              preSelectedAddons[matchingAddOn.addOnId] = addOn?.quantity || addOn?.qty || 1;
            }
          });
          setSelectedAddons(preSelectedAddons);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        showSnackbar(err?.message || err, "error");
      });
  }, [addOnModalOpen, selectedId, selectedAddOns]);

  const handleOptionChange = (variantId) => {
    setSelectedVariant(variantId);
    setSelectedAddons({});
  };

  // Qty stepper always starts at 1, regardless of what's already in the cart
  useEffect(() => {
    if (!addOnModalOpen) return;
    setItemQuantity(1);
  }, [addOnModalOpen, selectedVariant]);

  const handleIncreaseAddonQty = (addOnId) => {
    setSelectedAddons((prev) => ({ ...prev, [addOnId]: (prev[addOnId] || 0) + 1 }));
  };

  const handleDecreaseAddonQty = (addOnId) => {
    setSelectedAddons((prev) => {
      const currentQty = prev[addOnId] || 0;
      if (currentQty <= 1) {
        const next = { ...prev };
        delete next[addOnId];
        return next;
      }
      return { ...prev, [addOnId]: currentQty - 1 };
    });
  };

  const handleClose = () => {
    setAddOnModalOpen(false);
    setSelectedAddons({});
    setSelectedVariant("");
    setKitchenNotes("");
    setItemQuantity(1);
    setFocusedNavIndex(-1);
    setPricingMode("regular");
    setDiscountValue("");
    setDiscountType("percent");
  };

  // Prices
  const variantsSource = fetchVariantsFromAPI ? variantsFromApi : productDetails?.variants;
  const selectedVariantData = variantsSource?.find((v) => v?.variantId === selectedVariant);
  const channelData = selectedVariantData?.externalSalesChannels?.find(
    (ch) => ch?.salesChannelName === saleMode
  );
  const basePrice =
    channelData?.salePrice ||
    selectedVariantData?.inventorySync?.sellingPrice ||
    productDetails?.sellingPrice ||
    0;

  const addonTotal = useMemo(() => {
    let total = 0;
    particularAddonsList?.forEach((item) => {
      total += (selectedAddons[item.addOnId] || 0) * (item.additionalPrice || 0);
    });
    return total;
  }, [selectedAddons, particularAddonsList]);

  const finalItemPrice = useMemo(() => {
    if (pricingMode === "complimentary") return 0;
    if (pricingMode === "discount") {
      const val = parseFloat(discountValue) || 0;
      if (discountType === "percent") return Math.max(0, basePrice - (basePrice * val) / 100);
      return Math.max(0, basePrice - val);
    }
    return basePrice;
  }, [pricingMode, discountType, discountValue, basePrice]);

  const totalAmount = finalItemPrice + addonTotal;

  const handleAddItem = () => {
    if (!productDetails || !selectedVariant) return;
    const { barcodes, name, index, packaging_type, sellingUnit, sellingPrice, id } = productDetails;
    const addOn = particularAddonsList
      ?.filter((item) => selectedAddons[item.addOnId])
      .map((item) => ({ ...item, qty: selectedAddons[item.addOnId] }));
    onAddItem(
      barcodes,
      name,
      index,
      packaging_type,
      sellingUnit,
      selectedVariant,
      pricingMode === "complimentary" ? 0 : finalItemPrice,
      addOn,
      id,
      false,
      null,
      kitchenNotes || null,
      itemQuantity,
      pricingMode === "complimentary" // 14th arg: isComplimentary flag
    );
    handleClose();
  };

  useEffect(() => {
    if (!addOnModalOpen) {
      setParticularAddonsList([]);
      setSelectedAddons({});
      return;
    }
    const variantsSource = fetchVariantsFromAPI ? variantsFromApi : productDetails?.variants;
    if (!selectedVariant && !fetchVariantsFromAPI) {
      if (variantsSource?.length) setSelectedVariant(variantsSource[0]?.variantId);
    }
    if (selectedVariant) {
      const selectedVariantData =
        Array.isArray(variantsSource) &&
        variantsSource?.find((v) => v?.variantId === selectedVariant);
      setParticularAddonsList(selectedVariantData?.addOn || []);
      if (!fetchVariantsFromAPI) setSelectedAddons({});
    } else {
      setParticularAddonsList([]);
      if (!fetchVariantsFromAPI) setSelectedAddons({});
    }
  }, [addOnModalOpen, selectedVariant, productDetails, variantsFromApi, fetchVariantsFromAPI]);

  const groupedAddons = useMemo(() => {
    if (!particularAddonsList?.length) return {};
    return particularAddonsList.reduce((groups, addon) => {
      const groupName = addon?.groupName || "Other";
      if (!groups[groupName]) {
        groups[groupName] = {
          addons: [],
          min: addon?.min || 0,
          max: addon?.max || 0,
          selectionType: addon?.selectionType || "multiSelect",
        };
      }
      groups[groupName].addons.push(addon);
      return groups;
    }, {});
  }, [particularAddonsList]);

  const groupQuantities = useMemo(() => {
    const quantities = {};
    Object.entries(groupedAddons).forEach(([groupName, groupData]) => {
      let totalQty = 0;
      groupData.addons.forEach((addon) => {
        totalQty += selectedAddons[addon.addOnId] || 0;
      });
      quantities[groupName] = totalQty;
    });
    return quantities;
  }, [groupedAddons, selectedAddons]);

  const productImage =
    selectedVariantData?.images?.front || productDetails?.variants?.[0]?.images?.front || null;

  // Build the flat navigable list: [in-stock variants, ...all addons]
  const navigableVariants = useMemo(() => {
    return (variantsSource || []).filter((v) => {
      const ch = v?.externalSalesChannels?.find((c) => c?.salesChannelName === saleMode);
      return ch?.stockStatus !== "OUT_OF_STOCK";
    });
  }, [variantsSource, saleMode]);

  // Sync refs every render (stale-closure safety)
  focusedNavIndexRef.current = focusedNavIndex;
  selectedVariantRef.current = selectedVariant;
  selectedAddonsRef.current = selectedAddons;
  navigableVariantsRef.current = navigableVariants;
  filteredAddonsRef.current = particularAddonsList || [];
  groupQuantitiesRef.current = groupQuantities;
  handleAddItemRef.current = handleAddItem;

  // Auto-scroll focused nav item into view
  useEffect(() => {
    if (focusedNavIndex < 0 || !bodyRef.current) return;
    const el = bodyRef.current.querySelector(`[data-nav-index="${focusedNavIndex}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [focusedNavIndex]);

  // Reset nav focus when modal closes
  useEffect(() => {
    if (!addOnModalOpen) setFocusedNavIndex(-1);
  }, [addOnModalOpen]);

  // ── Keyboard handler ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!addOnModalOpen) return;

    const handleKeyDown = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      const isTyping = tag === "input" || tag === "textarea";

      const variants = navigableVariantsRef.current;
      const addons = filteredAddonsRef.current;
      const totalItems = variants.length + addons.length;
      const idx = focusedNavIndexRef.current;

      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        if (isTyping) return;
        e.preventDefault();
        setFocusedNavIndex((prev) => Math.min(prev + 1, totalItems - 1));
        return;
      }

      if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        if (isTyping) return;
        e.preventDefault();
        setFocusedNavIndex((prev) => Math.max(prev - 1, -1));
        return;
      }

      if ((e.key === "Enter" || e.key === " ") && idx >= 0) {
        if (isTyping) return;
        e.preventDefault();

        if (idx < variants.length) {
          const variant = variants[idx];
          if (variant) {
            setSelectedVariant(variant.variantId);
            setSelectedAddons({});
            setFocusedNavIndex(-1);
          }
        } else {
          const addon = addons[idx - variants.length];
          if (!addon) return;
          const qty = selectedAddonsRef.current[addon.addOnId] || 0;
          if (qty > 0) {
            setSelectedAddons((prev) => {
              const currentQty = prev[addon.addOnId] || 0;
              if (currentQty <= 1) {
                const next = { ...prev };
                delete next[addon.addOnId];
                return next;
              }
              return { ...prev, [addon.addOnId]: currentQty - 1 };
            });
          } else {
            const gName = addon.groupName || "Other";
            const gQty = groupQuantitiesRef.current[gName] || 0;
            const gMax = addon.max || 0;
            if (gMax > 0 && gQty >= gMax) return;
            setSelectedAddons((prev) => ({ ...prev, [addon.addOnId]: 1 }));
          }
        }
        return;
      }

      if (e.key === "Enter" && idx === -1 && !isTyping) {
        if (tag === "button") return;
        e.preventDefault();
        handleAddItemRef.current?.();
        return;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [addOnModalOpen]);

  const shortCode = selectedVariantData?.shortCode || productDetails?.variants?.[0]?.shortCode;

  return (
    <Modal open={addOnModalOpen} onClose={handleClose} sx={{ zIndex: 1400 }}>
      <div className="am-overlay" onClick={handleClose}>
        <div className="am-container" onClick={(e) => e.stopPropagation()}>
          {/* ══ LEFT COLUMN: image + pricing ══ */}
          <div className="am-left">
            <div className="am-image-wrap">
              {productImage ? (
                <img
                  src={productImage}
                  alt={itemName || productDetails?.name}
                  className="am-product-img"
                />
              ) : (
                <div className="am-image-placeholder">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#c0c7d0"
                    strokeWidth={1.2}
                    width="56"
                    height="56"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                </div>
              )}
              <div className="am-left-meta">
                <div className="am-meta-top-row">
                  <p className="am-product-name">{itemName || productDetails?.name || "Item"}</p>
                  {shortCode && <span className="am-shortcode-badge">{shortCode}</span>}
                </div>
                <p className="am-product-price">₹{basePrice}</p>
              </div>
            </div>

            {/* Pricing section */}
            <div className="am-pricing-section">
              <div className="am-pricing-header">
                <span className="am-pricing-title">Pricing</span>
                <span className="am-pricing-hint">Choose one</span>
              </div>

              {/* Regular */}
              <label
                className={`am-pricing-option${
                  pricingMode === "regular" ? " am-pricing-option--active" : ""
                }`}
              >
                <span className="am-option-label">Regular item</span>
                <input
                  type="radio"
                  name="pricingMode"
                  checked={pricingMode === "regular"}
                  onChange={() => {
                    setPricingMode("regular");
                    setDiscountValue("");
                  }}
                  className="am-radio"
                />
              </label>

              {/* Item discount */}
              <label
                className="am-pricing-option am-pricing-option--disabled"
                onClick={(e) => {
                  e.preventDefault();
                  showSnackbar("Feature is coming soon", "warning");
                }}
              >
                <span className="am-option-label">Item discount</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ color: "#9ca3af", flexShrink: 0 }}
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4l3 3" />
                </svg>
                <input
                  type="radio"
                  name="pricingMode"
                  checked={false}
                  disabled
                  style={{ display: "none" }}
                />
              </label>

              {pricingMode === "discount" && (
                <div className="am-discount-row">
                  <button
                    className={`am-discount-type-btn${discountType === "percent" ? " active" : ""}`}
                    onClick={() => setDiscountType("percent")}
                  >
                    %
                  </button>
                  <button
                    className={`am-discount-type-btn${discountType === "rupee" ? " active" : ""}`}
                    onClick={() => setDiscountType("rupee")}
                  >
                    ₹
                  </button>
                  <input
                    type="number"
                    min="0"
                    placeholder={discountType === "percent" ? "e.g. 10" : "e.g. 50"}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="am-discount-input"
                  />
                </div>
              )}

              {/* Complimentary */}
              <label
                className={`am-pricing-option${
                  pricingMode === "complimentary" ? " am-pricing-option--active" : ""
                }`}
              >
                <span className="am-option-label">Complimentary</span>
                <input
                  type="radio"
                  name="pricingMode"
                  checked={pricingMode === "complimentary"}
                  onChange={() => {
                    setPricingMode("complimentary");
                    setDiscountValue("");
                  }}
                  className="am-radio"
                />
              </label>
            </div>
          </div>

          {/* ══ RIGHT COLUMN ══ */}
          <div className="am-right">
            {/* Close button — top right only */}
            <button className="am-close-btn" onClick={handleClose} aria-label="Close">
              <XMarkIcon style={{ width: 18, height: 18 }} />
            </button>

            {/* Scrollable body */}
            <div className="am-right-body" ref={bodyRef}>
              {/* Variants section */}
              {(variantsSource?.length > 0 || isLoading) && (
                <div className="am-section">
                  <div className="am-section-header">
                    <span className="am-section-title">Choose Size</span>
                    <span className="am-badge am-badge-required">Required</span>
                  </div>
                  {isLoading ? (
                    <div className="am-loader">
                      <CircularProgress size={22} sx={{ color: "#0860E6" }} />
                    </div>
                  ) : !variantsSource?.length ? (
                    <p className="am-empty">No variants available</p>
                  ) : (
                    <div className="am-variant-grid">
                      {variantsSource?.map((variant) => {
                        const ch = variant?.externalSalesChannels?.find(
                          (c) => c?.salesChannelName === saleMode
                        );
                        const outOfStock = ch?.stockStatus === "OUT_OF_STOCK";
                        const price = ch?.salePrice || variant?.inventorySync?.sellingPrice || 0;
                        const isSelected = selectedVariant === variant?.variantId;
                        const navIdx = navigableVariants?.findIndex(
                          (v) => v?.variantId === variant?.variantId
                        );
                        const isNavFocused =
                          !outOfStock && navIdx >= 0 && focusedNavIndex === navIdx;
                        return (
                          <div
                            key={variant?.variantId}
                            data-nav-index={navIdx >= 0 ? navIdx : undefined}
                            tabIndex={outOfStock ? -1 : 0}
                            className={`am-variant-card${isSelected ? " am-variant-selected" : ""}${
                              outOfStock ? " am-variant-oos" : ""
                            }${isNavFocused ? " am-nav-focused" : ""}`}
                            onClick={() => !outOfStock && handleOptionChange(variant?.variantId)}
                            onKeyDown={(e) => {
                              if ((e.key === "Enter" || e.key === " ") && !outOfStock) {
                                e.preventDefault();
                                e.stopPropagation();
                                handleOptionChange(variant?.variantId);
                              }
                            }}
                          >
                            <div className="am-variant-info">
                              <span className="am-variant-name">{variant?.name}</span>
                              <span className="am-variant-price">₹{price}</span>
                            </div>
                            <div
                              className={`am-variant-radio${
                                isSelected ? " am-variant-radio--selected" : ""
                              }`}
                            >
                              {isSelected && <div className="am-variant-radio-dot" />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Add-ons section */}
              {(selectedVariant || isLoading) && (
                <div className="am-section">
                  <div className="am-section-header">
                    <span className="am-section-title">Add-ons</span>
                    <span className="am-badge am-badge-optional">Optional</span>
                  </div>
                  {isLoading ? (
                    <div className="am-loader">
                      <CircularProgress size={22} sx={{ color: "#0860E6" }} />
                    </div>
                  ) : particularAddonsList?.length === 0 ? (
                    <p className="am-empty">No add-ons available for this item</p>
                  ) : Object.keys(groupedAddons).length > 0 ? (
                    Object.entries(groupedAddons).map(([groupName, groupData]) => {
                      const { addons, max } = groupData;
                      const currentQty = groupQuantities[groupName] || 0;
                      const maxReached = max > 0 && currentQty >= max;
                      const hasGroupName =
                        groupName && !["Other", "null", "undefined"].includes(groupName);
                      return (
                        <div className="am-group" key={groupName}>
                          <div className="am-group-header">
                            {hasGroupName && <span className="am-group-name">{groupName}</span>}
                            {max > 0 && (
                              <span
                                className={`am-group-max${
                                  maxReached ? " am-group-max--reached" : ""
                                }`}
                              >
                                Max {max} add-ons
                              </span>
                            )}
                          </div>
                          <div className="am-addon-list">
                            {addons.map((item, idx) => {
                              const qty = selectedAddons[item.addOnId] || 0;
                              const isItemSelected = qty > 0;
                              const isOutOfStock = item?.stockStatus === "OUT_OF_STOCK";
                              const isNotServing =
                                item?.available === false || item?.isAvailable === false;
                              const isUnavailable = isOutOfStock || isNotServing;
                              const addonNavIdx =
                                navigableVariants?.length +
                                (particularAddonsList || []).findIndex(
                                  (a) => a.addOnId === item.addOnId
                                );
                              const isNavFocused = focusedNavIndex === addonNavIdx;
                              const isEven = idx % 2 === 0;
                              const pairItem = addons[isEven ? idx + 1 : null];
                              if (!isEven) return null; // render pairs from even index
                              return (
                                <div className="am-addon-row" key={item?.addOnId}>
                                  {/* Left cell */}
                                  <div
                                    data-nav-index={addonNavIdx}
                                    className={`am-addon-cell${
                                      isItemSelected ? " am-addon-cell--selected" : ""
                                    }${isUnavailable ? " am-addon-cell--unavailable" : ""}${
                                      isNavFocused ? " am-nav-focused" : ""
                                    }${maxReached && !isItemSelected ? " am-addon-cell--max" : ""}`}
                                    onClick={() => {
                                      if (isUnavailable) return;
                                      if (maxReached && !isItemSelected) {
                                        showSnackbar("Maximum Limit Reached", "warning");
                                        return;
                                      }
                                      isItemSelected
                                        ? null
                                        : setSelectedAddons((prev) => ({
                                            ...prev,
                                            [item?.addOnId]: 1,
                                          }));
                                    }}
                                  >
                                    <div className="am-addon-cell-top">
                                      <span className="am-addon-cell-name">{item?.title}</span>
                                      <span className="am-addon-cell-price">
                                        ₹{item?.additionalPrice || 0}
                                      </span>
                                    </div>
                                    {isOutOfStock && (
                                      <span className="am-addon-status-tag am-addon-status-tag--oos">
                                        ⊗ Out of stock
                                      </span>
                                    )}
                                    {!isOutOfStock && isNotServing && (
                                      <span className="am-addon-status-tag am-addon-status-tag--ns">
                                        ⊗ Not serving now
                                      </span>
                                    )}
                                    {isItemSelected && !isUnavailable && (
                                      <div
                                        className="am-addon-ctrl"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <button
                                          className="am-addon-ctrl-btn"
                                          onClick={() => handleDecreaseAddonQty(item?.addOnId)}
                                        >
                                          −
                                        </button>
                                        <span className="am-addon-ctrl-qty">{qty}</span>
                                        <button
                                          className={`am-addon-ctrl-btn${
                                            maxReached ? " am-addon-ctrl-btn--disabled" : ""
                                          }`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (maxReached) {
                                              showSnackbar("Maximum Limit Reached", "warning");
                                            } else {
                                              handleIncreaseAddonQty(item?.addOnId);
                                            }
                                          }}
                                        >
                                          +
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  {/* Right cell */}
                                  {pairItem ? (
                                    (() => {
                                      const pQty = selectedAddons[pairItem.addOnId] || 0;
                                      const pSelected = pQty > 0;
                                      const pOOS = pairItem?.stockStatus === "OUT_OF_STOCK";
                                      const pNS =
                                        pairItem?.available === false ||
                                        pairItem?.isAvailable === false;
                                      const pUnavail = pOOS || pNS;
                                      const pNavIdx =
                                        navigableVariants?.length +
                                        (particularAddonsList || []).findIndex(
                                          (a) => a?.addOnId === pairItem?.addOnId
                                        );
                                      const pNavFocused = focusedNavIndex === pNavIdx;
                                      return (
                                        <div
                                          data-nav-index={pNavIdx}
                                          className={`am-addon-cell${
                                            pSelected ? " am-addon-cell--selected" : ""
                                          }${pUnavail ? " am-addon-cell--unavailable" : ""}${
                                            pNavFocused ? " am-nav-focused" : ""
                                          }${
                                            maxReached && !pSelected ? " am-addon-cell--max" : ""
                                          }`}
                                          onClick={() => {
                                            if (pUnavail) return;
                                            if (maxReached && !pSelected) {
                                              showSnackbar("Maximum Limit Reached", "warning");
                                              return;
                                            }
                                            pSelected
                                              ? null
                                              : setSelectedAddons((prev) => ({
                                                  ...prev,
                                                  [pairItem?.addOnId]: 1,
                                                }));
                                          }}
                                        >
                                          <div className="am-addon-cell-top">
                                            <span className="am-addon-cell-name">
                                              {pairItem?.title}
                                            </span>
                                            <span className="am-addon-cell-price">
                                              ₹{pairItem?.additionalPrice || 0}
                                            </span>
                                          </div>
                                          {pOOS && (
                                            <span className="am-addon-status-tag am-addon-status-tag--oos">
                                              ⊗ Out of stock
                                            </span>
                                          )}
                                          {!pOOS && pNS && (
                                            <span className="am-addon-status-tag am-addon-status-tag--ns">
                                              ⊗ Not serving now
                                            </span>
                                          )}
                                          {pSelected && !pUnavail && (
                                            <div
                                              className="am-addon-ctrl"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <button
                                                className="am-addon-ctrl-btn"
                                                onClick={() =>
                                                  handleDecreaseAddonQty(pairItem?.addOnId)
                                                }
                                              >
                                                −
                                              </button>
                                              <span className="am-addon-ctrl-qty">{pQty}</span>
                                              <button
                                                className={`am-addon-ctrl-btn${
                                                  maxReached ? " am-addon-ctrl-btn--disabled" : ""
                                                }`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  if (maxReached) {
                                                    showSnackbar(
                                                      "Maximum Limit Reached",
                                                      "warning"
                                                    );
                                                  } else {
                                                    handleIncreaseAddonQty(pairItem?.addOnId);
                                                  }
                                                }}
                                              >
                                                +
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()
                                  ) : (
                                    <div className="am-addon-cell am-addon-cell--empty" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="am-empty">No add-ons available</p>
                  )}
                </div>
              )}

              {/* Notes to Kitchen */}
              <div className="am-section">
                <div className="am-section-header">
                  <span className="am-section-title">Notes to Kitchen</span>
                  <span className="am-badge am-badge-optional">Optional</span>
                </div>
                <textarea
                  className="am-notes"
                  placeholder="Add cooking or plating instructions"
                  rows={3}
                  value={kitchenNotes}
                  onChange={(e) => setKitchenNotes(e.target.value)}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="am-footer">
              <div className="am-total-block">
                <span className="am-total-label">Total Amount</span>
                <span className="am-total-amount">
                  {pricingMode === "complimentary" ? (
                    <>
                      <span className="am-total-strike">
                        ₹{(basePrice + addonTotal) * itemQuantity}
                      </span>
                      <span className="am-total-free"> ₹0</span>
                    </>
                  ) : (
                    <>₹{totalAmount * itemQuantity}</>
                  )}
                </span>
              </div>
              <div className="am-footer-actions">
                <div className="am-qty-selector">
                  <button
                    className="am-qty-btn"
                    onClick={() => setItemQuantity((prev) => Math.max(1, prev - 1))}
                    disabled={itemQuantity <= 1}
                  >
                    −
                  </button>
                  <span className="am-qty-value">{itemQuantity}</span>
                  <button
                    className="am-qty-btn"
                    onClick={() => setItemQuantity((prev) => prev + 1)}
                  >
                    +
                  </button>
                </div>
                <button className="am-add-btn" onClick={handleAddItem} disabled={!selectedVariant}>
                  Add Item ›
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AddonModal;
